import IProcedureDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureDetail';
import React, { useState } from 'react';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import KendoReactDropDownTree from '@Shared.Angular/kendo.react/KendoReactDropDownTree';
import { getService } from '@Client/runner.hooks/useService';
import ICategoryDetail from '@Shared.Angular/@types/core/contracts/queryModel/category/categoryDetail';
import IPublishProcedure from '@Shared.Angular/@types/core/contracts/commands/procedures/publishProcedure';
import SmartSelect from '@Shared.Angular/flowingly.components/smartselect/SmartSelect/SmartSelect';
import IFlowinglySearchResult from '@Shared.Angular/@types/core/contracts/queryModel/search/flowinglySearchResult';
import { SearchEntityType } from '@Shared.Angular/flowingly.services/flowingly.constants';

type Props = {
  procedure: IProcedureDetail;
  onClose: () => void;
  onPublish: (procedure: IProcedureDetail) => void;
};

const PublishProcedureModal = ({ procedure, onClose, onPublish }: Props) => {
  const procedureApiService = getService('procedureApiService');
  const categoryApiService = getService('categoryApiService');
  const flowinglyConstants = getService('flowinglyConstants');
  const notificationService = getService('notificationService');
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [categories, setCategories] = useState<ICategoryDetail[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<Guid | null>(
    procedure.categoryId
  );

  const [publishTo, setPublishTo] = useState<PublishType>(PublishType.EVERYONE);
  const [selectedActors, setSelectedActors] = useState<
    IFlowinglySearchResult[]
  >([]);
  const [publishedToActors, setPublishedToActors] = useState<
    IFlowinglySearchResult[] | null
  >(null);
  const [notifyUsers, setNotifyUsers] = useState(false);

  React.useEffect(() => {
    const fetchCategories = categoryApiService.getCategories();
    const fetchPublishedTo = procedureApiService.getProcedurePublishedTo(
      procedure.id
    );

    Promise.all([fetchCategories, fetchPublishedTo])
      .then(([fetchedCategories, fetchedPublishedTo]) => {
        setCategories(fetchedCategories);
        const isPublishedToEveryone =
          fetchedPublishedTo.length === 1 &&
          fetchedPublishedTo[0].searchEntityType === SearchEntityType.GROUP &&
          fetchedPublishedTo[0].displayName ===
            flowinglyConstants.EVERYONE_GROUP_NAME;
        if (isPublishedToEveryone) {
          setPublishedToActors([]);
        } else {
          setPublishedToActors(fetchedPublishedTo);
          if (fetchedPublishedTo.length > 0) {
            setPublishTo(PublishType.SPECIFIC_ACTORS);
          }
        }
      })
      .catch(onClose);
  }, []);

  const publishProcedure = () => {
    if (!isSelectedCategoryValid() || !isActorSelectionValid()) {
      return;
    }
    setIsRequestPending(true);
    const userIds =
      publishTo === PublishType.SPECIFIC_ACTORS
        ? selectedActors
            .filter((actor) => actor.searchEntityType === SearchEntityType.USER)
            .map((actor) => actor.id)
        : [];

    const groupIds =
      publishTo === PublishType.SPECIFIC_ACTORS
        ? selectedActors
            .filter(
              (actor) => actor.searchEntityType === SearchEntityType.GROUP
            )
            .map((actor) => actor.id)
        : [];

    const command: IPublishProcedure = {
      id: procedure.id,
      categoryId: selectedCategoryId,
      notifyUsers: notifyUsers,
      publishToEveryone: publishTo === PublishType.EVERYONE,
      userIds: userIds,
      groupIds: groupIds
    };
    procedureApiService
      .publishProcedure(command)
      .then((publishedProcedure) => {
        onPublish(publishedProcedure);
        notificationService.showSuccessToast(
          'Procedure published successfully'
        );
      })
      .finally(() => {
        setIsRequestPending(false);
      });
  };

  const categorySelected = (id: Guid) => {
    setSelectedCategoryId(id);
  };

  const isSelectedCategoryValid = () => {
    return selectedCategoryId !== null && selectedCategoryId !== EmptyGuid;
  };

  const isActorSelectionValid = () => {
    return (
      publishTo !== PublishType.SPECIFIC_ACTORS || selectedActors.length > 0
    );
  };

  return publishedToActors === null ? (
    <></>
  ) : (
    <Modal
      title="Publish SOP"
      id="publish-procedure-modal"
      actionButtonText={
        publishTo === PublishType.UNPUBLISH ? 'Unpublish' : 'Publish'
      }
      isActionDisabled={
        isRequestPending ||
        !isSelectedCategoryValid() ||
        !isActorSelectionValid()
      }
      onActionClick={publishProcedure}
      onCancelClick={onClose}
      onClose={onClose}
      size="md"
    >
      <link
        href="https://kendo.cdn.telerik.com/themes/7.0.1/material/material-main.css"
        rel="stylesheet"
        type="text/css"
      />
      <div className="row-title">Publish Category</div>
      <div className="category">
        <label>The category users will find the SOP in </label>
        <div className="category-dropdown">
          <KendoReactDropDownTree
            placeholder="Select Category..."
            categories={categories}
            className="modal-overlay"
            categoryClicked={categorySelected}
            dataItemKey="id"
            textField="name"
            subItemsField="subCategories"
            selectedId={selectedCategoryId || EmptyGuid}
          />
          {!isSelectedCategoryValid() && (
            <div className="error-msg">Please select a category</div>
          )}
        </div>
      </div>

      <div className="notify-container">
        <div>
          <input
            id="notifyUser"
            type="checkbox"
            checked={notifyUsers}
            disabled={publishTo === PublishType.UNPUBLISH}
            onChange={(e) => setNotifyUsers(e.target.checked)}
          />
          <label htmlFor="notifyUser"></label>
        </div>
        <div title="All users and members of teams the SOP is published to will be sent an email">
          Notify users <b>{procedure.name}</b> has been published?
        </div>
      </div>

      <div className="row-title">Publish To</div>
      <div>
        <input
          name="radio1"
          id="radios1"
          type="radio"
          radioGroup="publishTo"
          onChange={() => setPublishTo(PublishType.EVERYONE)}
          checked={publishTo === PublishType.EVERYONE}
        />
        <label htmlFor="radios1">
          Everyone
          <span className="label-description">
            Everyone in this organisation can view this SOP
          </span>
        </label>
      </div>
      <div>
        <input
          name="radio2"
          id="radios2"
          type="radio"
          radioGroup="publishTo"
          onChange={() => setPublishTo(PublishType.SPECIFIC_ACTORS)}
          checked={publishTo === PublishType.SPECIFIC_ACTORS}
        />
        <label htmlFor="radios2">
          Specific Users & Teams
          <span className="label-description">
            Choose who has access to this SOP
          </span>
        </label>
      </div>
      {procedure.published && (
        <div>
          <input
            name="radio3"
            id="radios3"
            type="radio"
            radioGroup="publishTo"
            onChange={() => setPublishTo(PublishType.UNPUBLISH)}
            checked={publishTo === PublishType.UNPUBLISH}
          />
          <label htmlFor="radios3">
            Unpublish this SOP
            <span className="label-description">
              Nobody will be able to view this SOP
            </span>
          </label>
        </div>
      )}
      {publishTo === PublishType.SPECIFIC_ACTORS && (
        <SmartSelect
          headerText="Publish List"
          infoText="List of people who are able to view this SOP"
          initialSelectedItems={publishedToActors}
          onChange={setSelectedActors}
        />
      )}
    </Modal>
  );
};

export default PublishProcedureModal;

enum PublishType {
  EVERYONE,
  SPECIFIC_ACTORS,
  UNPUBLISH
}
